import { Component, computed, inject, input, model, output, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { finalize } from 'rxjs';
import { CurrentContextService } from '../../../core/context/current-context.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import { FormSchemaModel } from '../../../shared/dynamic-form/form-schema.models';
import { DynamicApiService, DynamicEntity } from '../dynamic-api.service';
import {
  AiAuthoringMode,
  AiAuthoringWizardContext,
  AiFormDraftAcceptedEvent,
  AiSchemaDraftDetailApiResponse,
  AiSchemaDraftSummary,
  EntityDraft,
  EntityDraftApiResponse,
  FormDraft,
  FormDraftApiResponse
} from './ai-authoring.models';
import { EntityDraftPreviewComponent } from './entity-draft-preview.component';
import { FormDraftPreviewComponent } from './form-draft-preview.component';
import {
  entityDraftToEditable,
  formDraftToSchema,
  hasAiGeneratePermission,
  resolveAiAuthoringError
} from './ai-authoring.util';

type WizardStep = 'prompt' | 'preview' | 'loading';

@Component({
  standalone: true,
  selector: 'ain-ai-authoring-wizard',
  imports: [
    DatePipe,
    FormsModule,
    ButtonModule,
    DialogModule,
    SelectModule,
    TagModule,
    TextareaModule,
    AinFormFieldComponent,
    InfoHintComponent,
    AinStateComponent,
    EntityDraftPreviewComponent,
    FormDraftPreviewComponent
  ],
  providers: [MessageService],
  template: `
    <p-dialog
      [header]="dialogTitle()"
      [modal]="true"
      [visible]="visible()"
      (visibleChange)="onVisibleChange($event)"
      [style]="{ width: step() === 'preview' ? 'min(1100px, 96vw)' : 'min(720px, 94vw)' }"
      [draggable]="false"
      (onHide)="resetWizard()">
      @if (step() === 'prompt') {
        <div class="wizard-body">
          <ain-info-hint
            [titleKey]="mode() === 'entity' ? 'dynamic.aiAuthoring.entityPrompt.infoTitle' : 'dynamic.aiAuthoring.formPrompt.infoTitle'"
            [descriptionKey]="mode() === 'entity' ? 'dynamic.aiAuthoring.entityPrompt.infoDescription' : 'dynamic.aiAuthoring.formPrompt.infoDescription'"
            [bulletKeys]="mode() === 'entity'
              ? [
                  'dynamic.aiAuthoring.entityPrompt.infoBulletNatural',
                  'dynamic.aiAuthoring.entityPrompt.infoBulletOptions',
                  'dynamic.aiAuthoring.entityPrompt.infoBulletReview',
                  'dynamic.aiAuthoring.entityPrompt.infoBulletApply'
                ]
              : [
                  'dynamic.aiAuthoring.formPrompt.infoBulletNatural',
                  'dynamic.aiAuthoring.formPrompt.infoBulletEntityAlign',
                  'dynamic.aiAuthoring.formPrompt.infoBulletBuilder',
                  'dynamic.aiAuthoring.formPrompt.infoBulletApply'
                ]" />

          @if (recentDrafts().length) {
            <section class="recent-drafts">
              <h4>{{ t('dynamic.aiAuthoring.resume.title') }}</h4>
              <p class="recent-hint">{{ t('dynamic.aiAuthoring.resume.hint') }}</p>
              <div class="recent-list">
                @for (draft of recentDrafts(); track draft.id) {
                  <button
                    type="button"
                    class="recent-item"
                    [disabled]="resuming()"
                    (click)="resumeDraft(draft.id)">
                    <span class="recent-date">{{ draft.createdAtUtc | date:'short' }}</span>
                    @if (draft.usedFallback) {
                      <p-tag severity="warn" [value]="t('dynamic.aiAuthoring.resume.fallbackTag')" />
                    }
                    <span class="recent-expires">{{ t('dynamic.aiAuthoring.resume.expires') }}: {{ draft.expiresAtUtc | date:'short' }}</span>
                  </button>
                }
              </div>
            </section>
          }

          <ain-form-field [label]="t('dynamic.aiAuthoring.promptLabel')" [hint]="t('dynamic.aiAuthoring.promptHint')">
            <textarea
              pTextarea
              rows="6"
              [(ngModel)]="prompt"
              [placeholder]="t('dynamic.aiAuthoring.promptPlaceholder')"
              [maxlength]="8000"></textarea>
          </ain-form-field>

          <ain-form-field [label]="t('dynamic.aiAuthoring.labelLanguage')">
            <p-select
              [options]="labelLanguageOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="labelLanguage"
              [showClear]="true"
              appendTo="body" />
          </ain-form-field>

          @if (mode() === 'form') {
            <ain-form-field [label]="t('dynamic.aiAuthoring.layoutHint')">
              <p-select
                [options]="layoutHintOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="layoutHint"
                [showClear]="true"
                appendTo="body" />
            </ain-form-field>

            <ain-form-field [label]="t('dynamic.aiAuthoring.targetEntity')" [hint]="t('dynamic.aiAuthoring.targetEntityHint')">
              <p-select
                [options]="entityOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="targetEntityId"
                [showClear]="true"
                [filter]="true"
                appendTo="body" />
            </ain-form-field>
          }

          @if (errorMessage()) {
            <div class="error-panel">
              <ain-state type="error" [title]="t('dynamic.aiAuthoring.generateFailed')" [message]="errorMessage() ?? ''" />
              <button
                pButton
                type="button"
                severity="secondary"
                [label]="t('dynamic.aiAuthoring.retry')"
                (click)="generateDraft()"></button>
            </div>
          }
        </div>
      } @else if (step() === 'loading') {
        <ain-state type="loading" [title]="t('dynamic.aiAuthoring.generating')" [message]="t('dynamic.aiAuthoring.generatingHint')" />
      } @else if (step() === 'preview' && mode() === 'entity' && entityDraft()) {
        <ain-entity-draft-preview
          [(draft)]="entityDraft"
          [warnings]="warnings()"
          [usedFallback]="usedFallback()"
          [draftId]="draftId()"
          (cancelled)="close()"
          (discarded)="onDraftDiscarded()"
          (applied)="onEntityApplied($event)" />
      } @else if (step() === 'preview' && mode() === 'form' && formDraft()) {
        <ain-form-draft-preview
          [draft]="formDraft()!"
          [warnings]="warnings()"
          [usedFallback]="usedFallback()" />
      }
      <ng-template pTemplate="footer">
        @if (step() === 'prompt') {
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="close()"></button>
          <button
            pButton
            type="button"
            icon="pi pi-sparkles"
            [label]="t('dynamic.aiAuthoring.generate')"
            [disabled]="generating() || !prompt.trim()"
            (click)="generateDraft()"></button>
        } @else if (step() === 'preview' && mode() === 'form') {
          <button pButton type="button" severity="secondary" [label]="t('dynamic.aiAuthoring.back')" (click)="backToPrompt()"></button>
          <button
            pButton
            type="button"
            severity="danger"
            [label]="t('dynamic.aiAuthoring.discard')"
            [disabled]="discarding()"
            (click)="discardCurrentDraft()"></button>
          <button pButton type="button" icon="pi pi-arrow-right" [label]="t('dynamic.aiAuthoring.loadIntoBuilder')" (click)="acceptFormDraft()"></button>
        }
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .wizard-body { display: grid; gap: 14px; }
    .error-panel { display: grid; gap: 8px; justify-items: start; }
    .recent-drafts {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 12px 14px;
      display: grid;
      gap: 8px;
    }
    .recent-drafts h4 { margin: 0; font-size: 0.95rem; }
    .recent-hint { margin: 0; color: #6b7280; font-size: 0.875rem; }
    .recent-list { display: grid; gap: 6px; }
    .recent-item {
      display: flex;
      gap: 10px;
      align-items: center;
      flex-wrap: wrap;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 8px 10px;
      background: #fff;
      cursor: pointer;
      text-align: start;
    }
    .recent-item:disabled { opacity: 0.6; cursor: wait; }
    .recent-date { font-weight: 600; color: #111827; }
    .recent-expires { color: #6b7280; font-size: 0.85rem; }
  `]
})
export class AiAuthoringWizardComponent {
  private readonly api = inject(DynamicApiService);
  private readonly localization = inject(LocalizationService);
  private readonly messages = inject(MessageService);
  private readonly contextService = inject(CurrentContextService);

  readonly mode = input<AiAuthoringMode>('entity');
  readonly context = input<AiAuthoringWizardContext | null>(null);
  readonly visible = model(false);

  readonly formDraftAccepted = output<AiFormDraftAcceptedEvent>();
  readonly entityApplied = output<string>();
  readonly closed = output<void>();

  readonly step = signal<WizardStep>('prompt');
  readonly generating = signal(false);
  readonly resuming = signal(false);
  readonly discarding = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly warnings = signal<string[]>([]);
  readonly usedFallback = signal(false);
  readonly draftId = signal<string | null>(null);
  readonly entityDraft = model<EntityDraft | null>(null);
  readonly formDraft = signal<FormDraft | null>(null);
  readonly formSchema = signal<FormSchemaModel | null>(null);
  readonly entities = signal<DynamicEntity[]>([]);
  readonly recentDrafts = signal<AiSchemaDraftSummary[]>([]);

  prompt = '';
  labelLanguage: string | null = null;
  layoutHint: string | null = null;
  targetEntityId: string | null = null;

  readonly dialogTitle = computed(() =>
    this.mode() === 'entity'
      ? this.t('dynamic.aiAuthoring.entityTitle')
      : this.t('dynamic.aiAuthoring.formTitle')
  );

  readonly labelLanguageOptions = computed(() => [
    { label: this.t('dynamic.builder.language.en'), value: 'en-US' },
    { label: this.t('dynamic.builder.language.fa'), value: 'fa-IR' }
  ]);

  readonly layoutHintOptions = computed(() => [
    { label: this.t('dynamic.builder.layout.wizard'), value: 'wizard' },
    { label: this.t('dynamic.builder.layout.singleColumn'), value: 'singleColumn' },
    { label: this.t('dynamic.builder.layout.twoColumn'), value: 'twoColumn' },
    { label: this.t('dynamic.builder.layout.tabbed'), value: 'tabbed' }
  ]);

  readonly entityOptions = computed(() =>
    this.entities().map(entity => ({
      label: `${entity.displayName} (${entity.name})`,
      value: entity.id
    }))
  );

  onVisibleChange(value: boolean): void {
    this.visible.set(value);
    if (!value) {
      this.resetWizard();
      this.closed.emit();
    }
  }

  open(): void {
    if (!this.canGenerate()) {
      this.messages.add({ severity: 'warn', summary: this.t('common.noPermission') });
      return;
    }

    const ctx = this.context();
    if (ctx?.targetEntityId) {
      this.targetEntityId = ctx.targetEntityId;
    }

    this.visible.set(true);
    this.loadEntities();
    this.loadRecentDrafts();
  }

  close(): void {
    this.visible.set(false);
  }

  backToPrompt(): void {
    this.step.set('prompt');
    this.errorMessage.set(null);
    this.loadRecentDrafts();
  }

  generateDraft(): void {
    const trimmedPrompt = this.prompt.trim();
    if (!trimmedPrompt) {
      this.messages.add({ severity: 'warn', summary: this.t('dynamic.aiAuthoring.errors.prompt_required') });
      return;
    }

    this.generating.set(true);
    this.errorMessage.set(null);
    this.step.set('loading');

    if (this.mode() === 'entity') {
      this.api.generateEntityDraft({
        prompt: trimmedPrompt,
        labelLanguage: this.labelLanguage
      }).pipe(
        finalize(() => this.generating.set(false))
      ).subscribe({
        next: response => this.handleGenerateSuccess(response),
        error: (error: unknown) => {
          this.step.set('prompt');
          this.errorMessage.set(resolveAiAuthoringError(error, key => this.t(key)));
        }
      });
      return;
    }

    this.api.generateFormDraft({
      prompt: trimmedPrompt,
      layoutHint: this.layoutHint,
      targetEntityId: this.targetEntityId,
      targetEntityKey: this.resolveTargetEntityKey(),
      labelLanguage: this.labelLanguage
    }).pipe(
      finalize(() => this.generating.set(false))
    ).subscribe({
      next: response => this.handleGenerateSuccess(response),
      error: (error: unknown) => {
        this.step.set('prompt');
        this.errorMessage.set(resolveAiAuthoringError(error, key => this.t(key)));
      }
    });
  }

  resumeDraft(id: string): void {
    this.resuming.set(true);
    this.errorMessage.set(null);
    this.step.set('loading');

    this.api.getAiDraft(id).pipe(
      finalize(() => this.resuming.set(false))
    ).subscribe({
      next: detail => this.handleDraftLoaded(detail),
      error: (error: unknown) => {
        this.step.set('prompt');
        this.messages.add({
          severity: 'error',
          summary: resolveAiAuthoringError(error, key => this.t(key))
        });
      }
    });
  }

  discardCurrentDraft(): void {
    const id = this.draftId();
    if (!id) {
      this.backToPrompt();
      return;
    }

    this.discarding.set(true);
    this.api.discardAiDraft(id).pipe(
      finalize(() => this.discarding.set(false))
    ).subscribe({
      next: () => this.onDraftDiscarded(),
      error: (error: unknown) => {
        this.messages.add({
          severity: 'error',
          summary: resolveAiAuthoringError(error, key => this.t(key))
        });
      }
    });
  }

  onDraftDiscarded(): void {
    this.messages.add({ severity: 'success', summary: this.t('dynamic.aiAuthoring.discardSuccess') });
    this.draftId.set(null);
    this.entityDraft.set(null);
    this.formDraft.set(null);
    this.formSchema.set(null);
    this.backToPrompt();
  }

  acceptFormDraft(): void {
    const schema = this.formSchema();
    if (!schema) {
      return;
    }

    this.formDraftAccepted.emit({ schema, draftId: this.draftId() });
    this.messages.add({ severity: 'success', summary: this.t('dynamic.aiAuthoring.formPreview.loaded') });
    this.close();
  }

  onEntityApplied(entityId: string): void {
    this.entityApplied.emit(entityId);
    this.close();
  }

  canGenerate(): boolean {
    return hasAiGeneratePermission(this.contextService.context());
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private handleGenerateSuccess(response: EntityDraftApiResponse | FormDraftApiResponse): void {
    this.warnings.set(response.warnings ?? []);
    this.usedFallback.set(response.usedFallback);
    this.draftId.set(response.draftId);

    if (this.mode() === 'entity') {
      const editable = entityDraftToEditable((response as EntityDraftApiResponse).draft);
      this.entityDraft.set(editable);
      this.step.set('preview');
      return;
    }

    const draft = (response as FormDraftApiResponse).draft;
    this.applyFormDraftState(draft);
    this.step.set('preview');
  }

  private handleDraftLoaded(detail: AiSchemaDraftDetailApiResponse): void {
    if (detail.status !== 'Draft') {
      this.step.set('prompt');
      this.messages.add({ severity: 'warn', summary: this.t('dynamic.aiAuthoring.resume.unavailable') });
      this.loadRecentDrafts();
      return;
    }

    this.warnings.set(detail.warnings ?? []);
    this.usedFallback.set(detail.usedFallback);
    this.draftId.set(detail.id);

    if (this.mode() === 'entity' && detail.entityDraft) {
      this.entityDraft.set(entityDraftToEditable(detail.entityDraft));
      this.step.set('preview');
      return;
    }

    if (this.mode() === 'form' && detail.formDraft) {
      this.applyFormDraftState(detail.formDraft);
      this.step.set('preview');
      return;
    }

    this.step.set('prompt');
    this.messages.add({ severity: 'warn', summary: this.t('dynamic.aiAuthoring.resume.unavailable') });
  }

  private applyFormDraftState(draft: FormDraft): void {
    this.formDraft.set(draft);
    const culture = this.labelLanguage ?? this.localization.activeCulture();
    this.formSchema.set(formDraftToSchema(draft, culture));
  }

  private resolveTargetEntityKey(): string | null {
    const ctx = this.context();
    if (ctx?.targetEntityKey) {
      return ctx.targetEntityKey;
    }

    const selected = this.entities().find(entity => entity.id === this.targetEntityId);
    return selected?.name ?? null;
  }

  private loadEntities(): void {
    if (this.mode() !== 'form') {
      return;
    }

    this.api.listEntities().subscribe({
      next: items => this.entities.set(items ?? [])
    });
  }

  private loadRecentDrafts(): void {
    const type = this.mode() === 'entity' ? 'entity' : 'form';
    this.api.listAiDrafts(type).subscribe({
      next: items => this.recentDrafts.set(items ?? []),
      error: () => this.recentDrafts.set([])
    });
  }

  resetWizard(): void {
    this.step.set('prompt');
    this.generating.set(false);
    this.resuming.set(false);
    this.discarding.set(false);
    this.errorMessage.set(null);
    this.warnings.set([]);
    this.usedFallback.set(false);
    this.draftId.set(null);
    this.entityDraft.set(null);
    this.formDraft.set(null);
    this.formSchema.set(null);
    this.recentDrafts.set([]);
    this.prompt = '';
    this.labelLanguage = null;
    this.layoutHint = null;
    this.targetEntityId = null;
  }
}
